import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarDays } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { fmtEuro, fmtNum } from '../../lib/venduto';

const COLORE = { pranzo: '#D97706', cena: '#D94F3D' };

const METRICHE = [
  { key: 'coperti', label: 'Coperti totali', fmt: (v) => fmtNum(v) },
  { key: 'coperti_medi', label: 'Coperti medi', fmt: (v) => fmtNum(v, 1) },
  { key: 'fatturato', label: 'Fatturato', fmt: (v) => fmtEuro(v) },
  { key: 'coperto_medio', label: 'Coperto medio', fmt: (v) => fmtEuro(v) },
];

const TooltipGiorno = ({ active, payload, metrica }) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="rounded-lg border bg-background/95 p-2.5 text-xs shadow-lg space-y-1 min-w-[180px]">
      <p className="font-bold">{g.label}</p>
      {['pranzo', 'cena'].map((f) => (g.fasce[f] ? (
        <p key={f} className="flex justify-between gap-4">
          <span className="text-muted-foreground capitalize">{f}</span>
          <span className="font-semibold font-mono-numbers">{metrica.fmt(g.fasce[f][metrica.key])}</span>
        </p>
      ) : null))}
      <p className="flex justify-between gap-4 border-t pt-1">
        <span className="text-muted-foreground">Totale giorno</span>
        <span className="font-bold font-mono-numbers">{metrica.fmt(g[metrica.key])}</span>
      </p>
      <p className="text-[11px] text-muted-foreground">
        {fmtNum(g.giornate)} giornate · {fmtNum(g.coperti)} coperti · {fmtEuro(g.fatturato)}
      </p>
    </div>
  );
};

const Riga = ({ etichetta, dati, metrica, forte, testid }) => (
  <tr className={`border-b last:border-0 ${forte ? 'font-semibold bg-secondary/40' : ''}`} data-testid={testid}>
    <td className="py-1.5 whitespace-nowrap">{etichetta}</td>
    <td className="py-1.5 text-right font-mono-numbers text-muted-foreground">{fmtNum(dati.giornate)}</td>
    <td className="py-1.5 text-right font-mono-numbers">{fmtNum(dati.coperti)}</td>
    <td className="py-1.5 text-right font-mono-numbers">{fmtNum(dati.coperti_medi, 1)}</td>
    <td className="py-1.5 text-right font-mono-numbers">{fmtEuro(dati.fatturato)}</td>
    <td className="py-1.5 text-right font-mono-numbers">{fmtEuro(dati.coperto_medio)}</td>
  </tr>
);

/** Fatturato, coperti e coperto medio per giorno della settimana e fascia (pranzo/cena). */
export const GiornoServizioCard = ({ dati, className = '' }) => {
  const [metricaKey, setMetricaKey] = useState('coperti');
  const metrica = METRICHE.find((m) => m.key === metricaKey) || METRICHE[0];

  if (!dati?.per_giorno?.length) return null;

  const grafico = dati.per_giorno.map((g) => ({
    ...g,
    pranzo: g.fasce.pranzo?.[metrica.key] ?? null,
    cena: g.fasce.cena?.[metrica.key] ?? null,
  }));

  return (
    <Card className={className} data-testid="giorno-servizio-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary" /> Giorno e servizio
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1.5" data-testid="giorno-servizio-metriche">
          {METRICHE.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMetricaKey(m.key)}
              data-testid={`giorno-servizio-metrica-${m.key}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                metricaKey === m.key ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="h-56 lg:h-72 -ml-2" data-testid="giorno-servizio-grafico">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={grafico} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis
                tick={{ fontSize: 10 }}
                width={46}
                tickFormatter={(v) => (metrica.key === 'fatturato' && Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)}
              />
              <Tooltip content={<TooltipGiorno metrica={metrica} />} cursor={{ fill: '#F5F5F4' }} />
              <Bar dataKey="pranzo" name="Pranzo" fill={COLORE.pranzo} radius={[3, 3, 0, 0]} />
              <Bar dataKey="cena" name="Cena" fill={COLORE.cena} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.pranzo }} /> Pranzo
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.cena }} /> Cena
          </span>
          <span>Coperto medio = fatturato diviso i coperti</span>
        </div>

        <div className="overflow-x-auto -mx-4 px-4" data-testid="giorno-servizio-tabella">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="text-left font-medium py-1.5">Giorno e servizio</th>
                <th className="text-right font-medium py-1.5">Giornate</th>
                <th className="text-right font-medium py-1.5">Coperti</th>
                <th className="text-right font-medium py-1.5">Coperti medi</th>
                <th className="text-right font-medium py-1.5">Fatturato</th>
                <th className="text-right font-medium py-1.5">Coperto medio</th>
              </tr>
            </thead>
            <tbody>
              {dati.per_giorno.map((g) => (
                <React.Fragment key={g.dow}>
                  {['pranzo', 'cena'].map((f) => (g.fasce[f] ? (
                    <Riga
                      key={f}
                      etichetta={g.fasce[f].label}
                      dati={g.fasce[f]}
                      metrica={metrica}
                      testid={`giorno-servizio-riga-${g.dow}-${f}`}
                    />
                  ) : null))}
                  <Riga
                    etichetta={`${g.label} · totale`}
                    dati={g}
                    metrica={metrica}
                    forte
                    testid={`giorno-servizio-riga-${g.dow}-totale`}
                  />
                </React.Fragment>
              ))}
              <Riga
                etichetta="Totale periodo"
                dati={dati.totali}
                metrica={metrica}
                forte
                testid="giorno-servizio-riga-totale"
              />
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Mercoledì è chiuso. La domenica il pranzo è il servizio "Domenica pranzo". Gli eventi e le
          vendite fuori sala non hanno coperti e restano esclusi da questa vista.
        </p>
      </CardContent>
    </Card>
  );
};

export default GiornoServizioCard;
